import { Button, Card, Field, Input } from "@etain/ui";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";

export function PasswordRecoveryPage({
  mode,
}: {
  mode: "request" | "password_reset" | "invitation";
}) {
  const [token, setToken] = useState(() =>
    typeof window === "undefined"
      ? ""
      : (new URLSearchParams(window.location.hash.slice(1)).get("token") ?? ""),
  );
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [confirmation, setConfirmation] = useState("");
  const [formError, setFormError] = useState("");
  const action = useMutation({
    mutationFn: () =>
      mode === "request"
        ? api.post("/auth/password-reset/request", { email: email.trim() })
        : api.post(
            mode === "invitation" ? "/auth/invitation/complete" : "/auth/password-reset/complete",
            { token, password },
          ),
  });
  const resetAction = action.reset;
  useEffect(() => {
    if (mode === "request") return;
    const scrub = () =>
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + window.location.search,
      );
    const receiveLink = () => {
      const incoming = new URLSearchParams(window.location.hash.slice(1)).get("token");
      if (!incoming) return;
      setToken(incoming);
      setPassword("");
      setConfirmation("");
      setFormError("");
      resetAction();
      scrub();
    };
    // Initialization already read the fragment; remove it without consuming the token.
    if (window.location.hash) scrub();
    window.addEventListener("hashchange", receiveLink);
    return () => window.removeEventListener("hashchange", receiveLink);
  }, [mode, resetAction]);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");
    action.reset();
    if (mode !== "request" && password !== confirmation) {
      setFormError("Die Passwörter stimmen nicht überein.");
      return;
    }
    await action
      .mutateAsync()
      .then(() => {
        setPassword("");
        setConfirmation("");
      })
      .catch(() => undefined);
  }
  const title =
    mode === "request"
      ? "Passwort vergessen?"
      : mode === "invitation"
        ? "Einladung annehmen"
        : "Neues Passwort festlegen";
  return (
    <main className="login-page">
      <section className="login-brand" aria-label="ETAIN Hub">
        <div className="login-brand__inner">
          <img src="/brand/etain-logo.svg" alt="ETAIN Gesundheit" />
        </div>
      </section>
      <section className="login-form-wrap">
        <Card className="login-card">
          <h1>{title}</h1>
          {action.isSuccess ? (
            <p role="status">
              {mode === "request"
                ? "Falls ein passender Zugang besteht, erhalten Sie eine E-Mail mit den nächsten Schritten."
                : "Ihr Passwort wurde gespeichert. Bitte melden Sie sich mit Ihrem neuen Passwort an."}
            </p>
          ) : mode !== "request" && !/^[A-Za-z0-9_-]{43}$/.test(token) ? (
            <p role="alert">
              Der Link ist ungültig. Öffnen Sie den vollständigen Link aus Ihrer E-Mail erneut.
            </p>
          ) : (
            <>
              <p>
                {mode === "request"
                  ? "Geben Sie Ihre E-Mail-Adresse ein. Aus Sicherheitsgründen bestätigen wir keine Kontodaten."
                  : "Wählen Sie ein Passwort mit mindestens zwölf Zeichen. Ihre bisherigen Sitzungen werden beendet."}
              </p>
              {formError || action.isError ? (
                <p role="alert">{formError || action.error?.message}</p>
              ) : null}
              <form className="login-form" onSubmit={submit}>
                {mode === "request" ? (
                  <Field label="E-Mail-Adresse" htmlFor="recovery-email" required>
                    <Input
                      id="recovery-email"
                      type="email"
                      autoComplete="username"
                      autoCapitalize="none"
                      maxLength={254}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                    />
                  </Field>
                ) : (
                  <>
                    <Field label="Neues Passwort" htmlFor="recovery-password" required>
                      <Input
                        id="recovery-password"
                        type="password"
                        autoComplete="new-password"
                        minLength={12}
                        maxLength={1024}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        required
                      />
                    </Field>
                    <Field label="Passwort wiederholen" htmlFor="recovery-confirmation" required>
                      <Input
                        id="recovery-confirmation"
                        type="password"
                        autoComplete="new-password"
                        minLength={12}
                        maxLength={1024}
                        value={confirmation}
                        onChange={(event) => setConfirmation(event.target.value)}
                        required
                      />
                    </Field>
                  </>
                )}
                <Button type="submit" busy={action.isPending}>
                  {mode === "request" ? "Link anfordern" : "Passwort speichern"}
                </Button>
              </form>
            </>
          )}
          <p>
            <Link to="/anmelden">Zur Anmeldung</Link>
          </p>
          {mode !== "request" && !action.isSuccess ? (
            <p>
              {mode === "invitation" ? (
                "Bitten Sie Ihre Verwaltung bei Bedarf um eine neue Einladung."
              ) : (
                <Link to="/passwort-vergessen">Neuen Link anfordern</Link>
              )}
            </p>
          ) : null}
        </Card>
      </section>
    </main>
  );
}
